import React, { useCallback, useContext } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { SvgIcon } from '@mui/material';
import { COLORS } from 'theme/MainTheme';
import useStyles from './ActionEventButton.styles';
import { FilledButton } from 'components';
import { organizerContext } from '../../../OrganizerContext';
import { useDayEventContext } from './DayEventContext';
import { isEvent } from 'types/Event';
import { DayContentMode } from 'types/Organizer';
import { useWebUserContext } from 'contexts';
import {isEventOwner} from 'utils';

type EditOrViewEventButtonPureProps = {
  icon: typeof SvgIcon;
  handleEventEditClick: () => void;
};

const EditOrViewEventButtonPure = ({
  icon,
  handleEventEditClick,
}: EditOrViewEventButtonPureProps): JSX.Element => {
  const classes = useStyles();
  const iconComponent = React.createElement(icon, {
    className: classes.icon,
    htmlColor: COLORS.WHITE,
  });

  return (
    <FilledButton
      className={classes.button}
      color="blue"
      data-testid="edit-event-button"
      onClick={handleEventEditClick}
    >
      {iconComponent}
    </FilledButton>
  );
};

const EditOrViewEventButton = (): JSX.Element | null => {
  const { webUser } = useWebUserContext();
  const { dayEventOrTimeslot } = useDayEventContext();
  const { setDayContentMode, setEventToEdit } = useContext(organizerContext);

  const handleEventEditClick = useCallback(() => {
    if (isEvent(dayEventOrTimeslot)) {
      const mode = isEventOwner(dayEventOrTimeslot, webUser)
        ? DayContentMode.EDIT
        : DayContentMode.VIEW;

      setDayContentMode(mode);
      setEventToEdit(dayEventOrTimeslot);
    }
  }, [dayEventOrTimeslot, webUser, setDayContentMode, setEventToEdit]);

  if (!isEvent(dayEventOrTimeslot)) {
    return null;
  }

  const icon = isEventOwner(dayEventOrTimeslot, webUser)
    ? EditIcon
    : VisibilityIcon;

  return (
    <EditOrViewEventButtonPure
      icon={icon}
      handleEventEditClick={handleEventEditClick}
    />
  );
};

export default EditOrViewEventButton;
